/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import type { AxiosRequestConfig } from 'axios';

import Constants from '../constants';
import Utils from '../utils/utils';
import * as RuleStore from '../rules/rules-store';
import DataApiUtils from '../data-view/data-api-utils';
import MessagingService from '../messagingservice/manager';
import CascadeManager from '../cascade/manager';
import { fetch } from '../service_broker/fetch';
import { resolveRestApi } from '../router/app-router';

import Data from './data';
import type {
  PageDataAsyncOptions,
  DataStore,
  Parameters,
  Query,
  Paging,
  SharedDataObject,
  Subscribers,
  DataPageRecord,
  DataStoreMeta,
  DataPageSubscribers
} from './types';
// Extended interface for streaming support
interface ExtendedPageDataAsyncOptions extends PageDataAsyncOptions {
  enableStreaming?: boolean;
  streamOptions?: Partial<AxiosRequestConfig>;
}

/** The Datapage Class contains utility APIs to retrieve data from data pages. */
class DataPageUtils {
  isCacheEnabled: boolean;
  subscribers: Subscribers;
  dataPageSubscribers: DataPageSubscribers;
  dataStore: DataStore;
  dataStoreMeta: DataStoreMeta;
  subscribedDataPageNames: Map<string, { count: number; messagingSubscriptionId: string | undefined }>;
  SUBSCRIPTION_STATUS_FAILED = 'FAILED';
  SUBSCRIPTION_STATUS_SUCCESS = 'SUCCESS';
  DATAPAGE_NOT_CACHED = 'Data Page Not Cached';
  DUPLICATE_SUBSCRIPTION_ID = 'Duplicate Subscription Id';
  MISSING_MANDATORY_FIELD = 'Missing Mandatory Field(s)';

  constructor() {
    this.dataStore = {};
    this.dataStoreMeta = {};

    /*
     * {
     *  "<datapageName>": {
     *    "callback": {
     *      "<subId>": fn
     *    }
     *  }
     * }
     */
    this.subscribers = {};
    this.dataPageSubscribers = {};
    this.isCacheEnabled = true;

    this.subscribedDataPageNames = new Map();
  }

  /**
   * API to get data page results from cache
   * @param dataPageName data page name
   * @param parameters parameters associated to it.
   * parameters: {
   *  "paramName": "paramValue"
   * }
   * @param paging object containing information about the paging
   * paging: {
   *  "pageNumber": 2,
   *  "pageSize": 60
   * }
   * @param query object containing information about the list of columns, filters
   * @private
   */
  getData(dataPageName: string, parameters: Parameters, paging: Paging, query: Query) {
    const dataPageWithHash = this.generateHash(dataPageName, parameters, query);
    if (this.dataStore[dataPageName] && this.dataStore[dataPageName][dataPageWithHash]) {
      const dataInstance = this.dataStore[dataPageName][dataPageWithHash] as Data;

      if (dataInstance.isDirty) {
        return [];
      }
      return dataInstance.getRecords(paging);
    }
    return [];
  }

  /**
   * API to get list type of data page results
   * @param dataPageName name of the list type of data page whose results to be fetched
   * @param context context name
   * @param parameters object containing parameters associated to the datapage.
   * @param paging object containing information about the paging
   * @param query object containing information about the list of columns, filters
   * @param options Javascript object containing optional preferences for fetching data page results<br>
   * {
   *  invalidateCache: true,
   *  purgeDataPageCache: true
   * }
   * @returns response object with list type datapage results
   * @example  <caption>In the example below, the API retrieves the first 10 records of D_EmployeeList</caption>
   * const dataViewName = "D_EmployeeList";
   * const parameters = {
   * "dept": "Engineering"
   * };
   * const paging = {
   *  "pageNumber":1,
   *  "pageSize":10
   * };<br>
   * const query = {
   *  "distinctResultsOnly": true,
   *  "select": [
   *   {
   *     "field": "Name"
   *    },
   *   {
   *    "field": "Role"
   *   },
   *   {
   *    "field": "Gender"
   *   }
   *  ]
   * };
   * const context = "app/primary_1";
   * PCore.getDataPageUtils().getDataAsync(dataViewName, context, parameters, paging, query);
   * .then(response => {
   *  // The response of this API is as shown below:
   *  {
   *      data: [
   *        {
   *          "Name" : "Mark D",
   *          "Role" : "Software Engineer",
   *          "Gender" "Male"
   *        },
   *        {
   *          "Name" : "Lara",
   *          "Role" : "Electrician",
   *          "Gender" "Female"
   *        }
   *      ]
   *      fetchDateTime: "2020-06-29T11:06:24.329Z"
   *      pageNumber: 1
   *      pageSize: 10
   *  }
   * })
   * .catch(error => {
   *    console.log(error);
   * });
   *
   */

  async getDataAsync(
    dataPageName: string,
    context: string | undefined,
    parameters: Parameters = {},
    paging?: Paging,
    query?: Query,
    options: PageDataAsyncOptions = {}
  ) {
    if (options.purgeDataPageCache) {
      this.dataStore[dataPageName] = {};
      this._removeDataStoreMetaEntry(dataPageName);
    }
    if (!this.isCacheable(dataPageName, query) || options.invalidateCache) {
      const response = await this.fetchData(dataPageName, parameters, paging, query, context, options);
      const data = response?.data?.data ?? [];
      return {
        ...response.data,
        data,
        pageNumber: response?.data?.pageNumber,
        pageSize: response?.data?.pageSize,
        queryStats: response?.data?.queryStats,
        status: response?.status
      };
    }
    const results = this.getData(dataPageName, parameters, paging, query);
    if (results.length > 0) {
      return Promise.resolve({ data: results });
    }
    const serverResults = await this.fetchData(dataPageName, parameters, paging, query, context, options);
    return {
      ...serverResults.data,
      data: this.mergeDataStore(dataPageName, parameters, paging, query, serverResults),
      pageNumber: serverResults?.data?.pageNumber,
      pageSize: serverResults?.data?.pageSize,
      queryStats: serverResults?.data?.queryStats,
      status: serverResults?.status
    };
  }

  /**
   * API to get page type of data page results
   * @param dataPageName name of the page type of data page whose results to be fetched
   * @param context name of the context
   * "app/primary_1"
   * @param parameters object containing parameters associated to data page.
   * parameters: {
   *  "paramName": "paramValue"
   * }
   * @param options Javascript object containing optional preferences for fetching data page results
   * {
   *  invalidateCache: true,
   *  enableStreaming: true,
   *  streamOptions: { responseType: 'stream', adapter: 'fetch', headers: { Accept: 'text/event-stream' } }
   * }
   * @returns response object with page type datapage result
   * @example  <caption>In the example below, the API retrieves the D_FollowedBugsCount page type datapage result</caption>
   *  const dataViewName = "D_FollowedBugsCount";
   *  const parameters = {
   *  "ID": "12311"
   *` };
   *  const context = "app/primary_1";
   *  const options = {
   *   invalidateCache: true,
   *   enableStreaming: true
   *  }
   * PCore.getDataPageUtils().getPageDataAsync(dataViewName, context, parameters, options);
   * .then(response => {
   *  // The response of this API is as shown below:
   *  {
   *    "MyFollowedBugsCount":16,
   *    "pzLoadTime":"August 25, 2022 9:50:59 PM UTC",
   *    "pzPageNameHash":"_pa42316787137117pz"}
   * })
   * .catch(error => {
   *    console.log(error);
   * });
   */
  async getPageDataAsync(
    dataPageName: string,
    context?: string,
    parameters: Parameters = {},
    options: ExtendedPageDataAsyncOptions = {}
  ) {
    const dataPageWithHash = this.generateHash(dataPageName, parameters, null);
    if (options.purgeDataPageCache) {
      this.dataStore[dataPageName] = {};
      this._removeDataStoreMetaEntry(dataPageName);
    }
    if (
      this.isCacheable(dataPageName) &&
      this.dataStore[dataPageName] &&
      this.dataStore[dataPageName][dataPageWithHash]
    ) {
      if (options?.invalidateCache) {
        delete this.dataStore[dataPageName][dataPageWithHash];
        this._removeDataStoreMetaEntry(dataPageName, parameters, dataPageWithHash);
      } else {
        return Promise.resolve(this.dataStore[dataPageName][dataPageWithHash]);
      }
    }
    const { restApi: restUrl, reqType } = resolveRestApi(Constants.GET_PAGE_DATA, {
      dataViewName: dataPageName,
      dataViewParameters: encodeURIComponent(JSON.stringify(parameters))
    }) as { restApi: string; reqType: string };

    const reqConfig = {
      method: reqType
    };

    if (options.enableStreaming) {
      const streamOptions = options.streamOptions || {};
      Object.assign(reqConfig, {
        responseType: 'stream',
        adapter: 'fetch',
        headers: { Accept: 'text/event-stream' },
        ...streamOptions
      });
    }

    const getPageDataUrl =
      restUrl && parameters && Object.keys(parameters).length === 0 ? restUrl.split('?')[0] : restUrl;
    return fetch(getPageDataUrl, reqConfig, context).then((response: { data: object }) => {
      if (this.isCacheable(dataPageName)) {
        this.dataStore[dataPageName] = {
          ...this.dataStore[dataPageName],
          [dataPageWithHash]: response.data
        };
        this._addDataStoreMetaEntry(dataPageName, parameters, dataPageWithHash);
      }
      if (options.enableStreaming) {
        return response;
      }
      return response.data;
    });
  }

  /**
   * Subscribes to updates to a data page
   * @param {string} subscriptionId Unique ID assigned to a subscription
   * @param {Function} callback callback Function to be executed whenever a data page is updated
   * @param {string} dataPageName dataPageName Name of the data page that is being subscribed to
   * @param {object} [parameters = {}] (Optional), object containing parameters associated with the data page
   * @returns {boolean} Returns the Boolean value true if the data page is subscribed to successfully
   * @example <caption>In the example below, the API subscribes to any updates to the D_EmployeeList data page for the Engineering department</caption>
   * const subscriptionId = "900150983cd24fb0d6963f7d28e17f72";
   * const callback = function () {
   *  const updatedEmployeesList = PCore.getDataPageUtils().getDataAsync("D_EmployeeList", "app/primary_1", {"dept": "Engineering"});
   * }
   * const dataPageName = "D_EmployeeList";
   * const parameters = {
   * "department": "Engineering"
   * };
   * const isSubscribed = PCore.getDataPageUtils().subscribeToDataPageUpdates(subscriptionId, callback, dataViewName, parameters);
   * if(isSubscribed){
   *   console.info('Subscription successful');
   * }else{
   *   console.info('Subscription failure');
   * }
   */
  subscribeToDataPageUpdates(
    subscriptionId: string,
    callback: (...args: any) => any,
    dataPageName: string,
    parameters: Parameters = {}
  ) {
    if (!subscriptionId || !callback || !dataPageName) {
      return false;
    }
    const dataInstanceHash = this.generateHash(dataPageName, parameters, null);
    this.dataPageSubscribers[dataPageName] ??= {};
    const dataPageSubscriberObject = this.dataPageSubscribers[dataPageName];
    dataPageSubscriberObject[dataInstanceHash] ??= {};
    const dataPageInstanceSubscribers = dataPageSubscriberObject[dataInstanceHash];
    this.subscribeToMessageService(dataPageName, subscriptionId);
    if (!dataPageInstanceSubscribers[subscriptionId]) {
      dataPageInstanceSubscribers[subscriptionId] ??= callback;
      return true;
    }
    return false;
  }

  /**
   * Unsubscribes to updates to a data page
   * @param {string} subscriptionId Unique ID assigned to a subscription
   * @param {string} dataPageName Name of the data page that is being unsubscribed from
   * @param {object} [parameters = {}] (Optional), object containing parameters associated with the data page
   * @returns {boolean} Returns the Boolean value true if the data page is unsubscribed from successfully
   * @example  <caption>In the example below, the API unsubscribes to updates to the D_EmployeeList data page for the Engineering department</caption>
   * const subscriptionId = "900150983cd24fb0d6963f7d28e17f72";
   * const dataPageName = "D_EmployeeList";
   * const parameters = {
   * "dept": "Engineering"
   * };
   * const isUnsubscribed = PCore.getDataPageUtils().unsubscribeToDataPageUpdates(subscriptionId, dataPageName, parameters);
   * if(isUnsubscribed){
   *   console.info('Successfully unsubscribed ');
   * }else{
   *   console.info('Failure during unsubscription');
   * }
   */
  unsubscribeToDataPageUpdates(subscriptionId: string, dataPageName: string, parameters: Parameters = {}) {
    if (!subscriptionId || !dataPageName) {
      return false;
    }
    const dataInstanceHash = this.generateHash(dataPageName, parameters, null);
    delete this.dataPageSubscribers[dataPageName]?.[dataInstanceHash]?.[subscriptionId];

    // Decrement subscriber count for this dataPageName. If it reaches zero, remove the entry
    const subscriptionEntry = this.subscribedDataPageNames.get(dataPageName);
    const currentCount = subscriptionEntry?.count ?? 0;
    if (currentCount <= 1) {
      if (subscriptionEntry?.messagingSubscriptionId) {
        MessagingService.unsubscribe(subscriptionEntry.messagingSubscriptionId);
      }
      this.subscribedDataPageNames.delete(dataPageName);
    } else if (subscriptionEntry) {
      this.subscribedDataPageNames.set(dataPageName, { ...subscriptionEntry, count: currentCount - 1 });
    }

    return true;
  }

  // Backwards-compatible alias for older callers/tests

  unsubscribeToDatapageUpdates(subscriptionId: string, dataPageName?: string, parameters: Parameters = {}) {
    // If dataPageName is not provided, let the underlying method handle validation and return false
    return this.unsubscribeToDataPageUpdates(subscriptionId, dataPageName ?? '', parameters);
  }

  _isDataPageCached(dataPageName: string, parameters: Parameters) {
    if (parameters && Utils.isObjectEmpty(parameters)) {
      return !!this.dataStore[dataPageName];
    }
    const dataInstanceHash = this.generateHash(dataPageName, parameters, null);

    return !!this.dataStoreMeta[dataPageName]?.[dataInstanceHash]?.length;
  }

  updateDataPageStore(sharedDataPages: SharedDataObject = {}) {
    Object.keys(sharedDataPages).forEach((dataPageName) => {
      if (!this.dataStore[dataPageName]) {
        this.dataStore[dataPageName] = {};
      }

      const pages = sharedDataPages[dataPageName];

      Object.keys(pages).forEach((hashDpName) => {
        if (this.dataStore[dataPageName][hashDpName]) {
          const dataInstance = this.dataStore[dataPageName][hashDpName] as { mergeRecords: Function };
          dataInstance.mergeRecords(sharedDataPages[dataPageName][hashDpName].pxResults);
        } else {
          const dataInstance = new Data();
          this.dataStore[dataPageName][hashDpName] = dataInstance;
          dataInstance.mergeRecords(sharedDataPages[dataPageName][hashDpName].pxResults || []);
        }
      });
    });
  }

  _addDataStoreMetaEntry(dataPageName: string, parameters: Parameters, dataPageWithHash: string) {
    const dataStoreMetaKey = this.generateHash(dataPageName, parameters, null);
    this.dataStoreMeta[dataPageName] ??= {};
    const dataStoreMetaDataPageObject = this.dataStoreMeta[dataPageName];
    dataStoreMetaDataPageObject[dataStoreMetaKey] ??= [];
    const dataStoreMetaEntryList = dataStoreMetaDataPageObject[dataStoreMetaKey];

    if (!dataStoreMetaEntryList.includes(dataPageWithHash)) {
      dataStoreMetaEntryList.push(dataPageWithHash);
    }
  }

  _removeDataStoreMetaEntry(dataPageName: string, parameters?: Parameters, dataPageWithHash?: string) {
    if (dataPageName && !parameters && !dataPageWithHash) {
      this.dataStoreMeta[dataPageName] = {};
    }
    if (dataPageName && parameters && dataPageWithHash) {
      const dataStoreMetaKey = this.generateHash(dataPageName, parameters, null);
      this.dataStoreMeta[dataPageName][dataStoreMetaKey] = this.dataStoreMeta[dataPageName][dataStoreMetaKey].filter(
        (entry) => entry !== dataPageWithHash
      );
    }
  }

  mergeDataStore(
    dataPageName: string,
    parameters: Parameters,
    paging?: Paging,
    query?: Query,
    results?: { data?: { data: { [key: string]: any }[] } }
  ): { [key: string]: any }[] {
    const dataPageWithHash = this.generateHash(dataPageName, parameters, query);
    const data = results?.data?.data as DataPageRecord[];
    if (!data || data.length === 0) {
      return [];
    }

    if (!this.dataStore[dataPageName]) {
      this.dataStore[dataPageName] = {};
    }

    if (!this.dataStore[dataPageName][dataPageWithHash]) {
      this.dataStore[dataPageName][dataPageWithHash] = new Data();
    }

    const dataInstance = this.dataStore[dataPageName][dataPageWithHash] as Data;
    dataInstance.mergeRecords(data, paging);
    dataInstance.isDirty = false;
    this._addDataStoreMetaEntry(dataPageName, parameters, dataPageWithHash);
    return dataInstance.getRecords(paging);
  }

  isDataPage(propertyRef = '') {
    return propertyRef.startsWith('D_') || propertyRef.startsWith('Declare_');
  }

  subscribeToUpdate(
    dataPageName: string,
    dependentFields: string[],
    context: string,
    pageReference: string,
    callback: any,
    subscriptionId: string
  ) {
    this.subscribers[dataPageName] = this.subscribers[dataPageName] || {};
    this.subscribers[dataPageName].callback = this.subscribers[dataPageName].callback || {};

    if (subscriptionId && !this.subscribers[dataPageName].callback?.[subscriptionId]) {
      this.subscribers[dataPageName].callback[subscriptionId] = callback;
    }
    this.subscribeToCascadeUpdates(dataPageName, dependentFields, context, pageReference, subscriptionId);
  }

  unsubscribe(
    dataPageName: string,
    dependentFields: string[],
    context: string,
    pageReference: string,
    subscriptionId: string
  ) {
    delete this.subscribers[dataPageName]?.callback?.[subscriptionId];
    if (dependentFields && dependentFields.length > 0) {
      CascadeManager.unRegisterFields(context, pageReference, dependentFields, subscriptionId);
    }
  }

  /**
   * Message received from web socket service.
   * @param data =  {message, msgId}  format
   * {
   *  matcher: "DATAPAGE_UPDATED",
   *  message: {
   *      "datapage": "D_Test"
   *  }
   * }
   * @private
   */
  handleMessage(data: { msgId?: string; message: { datapage: string; parameters: string } }) {
    const { msgId, message } = data;
    const { datapage, parameters = '' } = message;

    const datapageCallback = this.subscribers[datapage];
    this.markDataInstanceDirty(datapage, parameters === '' ? null : JSON.parse(parameters));
    if (msgId) {
      this._executeDataPageSubscriptions(data);
    }
    if (!msgId && datapageCallback?.callback) {
      Object.keys(datapageCallback.callback).forEach((subId) => {
        datapageCallback.callback?.[subId](data.message);
      });
    }
  }

  markDataInstanceDirty(dataPageName: string, parameters: Parameters) {
    let dataStoreMetaKey;
    let cachedDataPageEntries: string[];
    if (parameters) {
      dataStoreMetaKey = this.generateHash(dataPageName, parameters, null);
      cachedDataPageEntries = this.dataStoreMeta[dataPageName]?.[dataStoreMetaKey];
      cachedDataPageEntries?.push(this.generateHash(dataPageName, null));
      if (!cachedDataPageEntries) {
        return;
      }
    }

    if (this.dataStore[dataPageName]) {
      const dataInstances = Object.keys(this.dataStore[dataPageName]);
      dataInstances
        .filter((dataPageHash) =>
          parameters && cachedDataPageEntries ? cachedDataPageEntries.includes(dataPageHash) : true
        )
        .forEach((dataPageHash) => {
          this.dataStore[dataPageName][dataPageHash].isDirty = true;
        });
    }
  }

  _executeDataPageSubscriptions(data: { msgId?: string; message: { datapage: string; parameters: string } }) {
    const { message } = data;
    const { datapage, parameters } = message;
    const dataPageParameters = parameters ? JSON.parse(parameters) : null;
    if (!this.dataPageSubscribers[datapage]) {
      return;
    }
    if (!dataPageParameters) {
      const dataPageSubscriberObject = this.dataPageSubscribers[datapage];
      Object.keys(dataPageSubscriberObject).forEach((dataInstanceHash) => {
        const dataPageInstanceSubscribers = dataPageSubscriberObject[dataInstanceHash];
        Object.keys(dataPageInstanceSubscribers).forEach((subscriberId) => {
          dataPageSubscriberObject[dataInstanceHash][subscriberId](data.message);
        });
      });
    } else {
      const subsKeys: string[] = [];
      subsKeys.push(this.generateHash(datapage, dataPageParameters, null));
      subsKeys.push(this.generateHash(datapage, null, null));
      subsKeys.forEach((subsKey) => {
        const dataPageSubscribers = this.dataPageSubscribers[datapage]?.[subsKey] ?? {};
        Object.keys(dataPageSubscribers).forEach((subscriptionId) => {
          dataPageSubscribers[subscriptionId](data.message);
        });
      });
    }
  }

  isPerInteraction(dataPageName: string) {
    const dataPageMetadata = RuleStore.getDataPageMetadata(dataPageName);
    return !dataPageMetadata || dataPageMetadata.refreshStrategy?.type === 'reloadOncePerInteraction';
  }

  /**
   * disable or enable the cache for
   * @param cacheDisable value whether Cache Should be disabled or enabled
   * @example  <caption>In the example below, the API set the value to disable or enable the cache. </caption>
   *  PCore.getDataPageUtils().disableCache(true)
   * @private
   */
  disableCache(cacheDisable: boolean) {
    this.isCacheEnabled = !cacheDisable;
  }

  private generateHash(dataPageName: string, parameters: Parameters, query?: Query) {
    const dataPageMetadata = RuleStore.getDataPageMetadata(dataPageName);
    let dataPageMetadataParameters: { name: string; defaultValue?: string }[] = [];
    if (!dataPageMetadata) {
      dataPageMetadataParameters = Object.keys(parameters || {}).map((val) => ({
        name: val
      }));
    }
    const dataPageParameters = dataPageMetadata?.parameters || dataPageMetadataParameters;

    let hashDigest = '';
    let dataPageWithHash = dataPageName;
    const propValues: (string | number | null | undefined)[] = [];

    dataPageParameters.forEach((param: { name: string; defaultValue?: string }) => {
      if (parameters?.[param.name]) {
        propValues.push(parameters[param.name]);
      } else {
        propValues.push(param.defaultValue ?? Constants.NULL_STRING);
      }
    });

    if (query) {
      propValues.push(JSON.stringify(query));
    }

    if (propValues.join(Constants.HASH_DELIMITER)) {
      hashDigest = Utils.getHashedString(propValues.join(Constants.HASH_DELIMITER));
      dataPageWithHash = dataPageName + Constants.UNDERSCORE_STRING + hashDigest;
    }
    return dataPageWithHash;
  }

  private async fetchData(
    dataPageName: string,
    parameters: Parameters,
    paging: Paging,
    query: Query,
    context: string | undefined,
    options: PageDataAsyncOptions
  ) {
    const { additionalApiParams: { useExtendedTimeout = false, includeTotalCount = false } = {}, ...otherOptions } =
      options;
    const payload: {
      dataViewParameters?: Parameters;
      includeTotalCount?: boolean;
      useExtendedTimeout?: boolean;
      paging?: Paging;
      query?: Query;
    } = {
      dataViewParameters: parameters
    };

    if (includeTotalCount) {
      payload.includeTotalCount = true;
    }
    if (useExtendedTimeout) {
      payload.useExtendedTimeout = true;
    }
    if (paging) {
      payload.paging = paging;
    }
    if (query) {
      payload.query = query;
    }
    return DataApiUtils.getData(dataPageName, payload, context, otherOptions);
  }

  private handleCascadeUpdate() {
    const { dataPageName, scope } = this as unknown as { dataPageName: string; scope: DataPageUtils };
    scope.handleMessage({
      message: {
        datapage: dataPageName,
        parameters: ''
      }
    });
  }

  private subscribeToCascadeUpdates(
    dataPageName: string,
    dependentFields: string[],
    context: string,
    pageReference: string,
    subscriptionId: string
  ) {
    // Subscribe to parameter updates
    if (dependentFields && dependentFields.length > 0) {
      CascadeManager.registerFields(
        context,
        pageReference,
        dependentFields,
        this.handleCascadeUpdate.bind({ dataPageName, scope: this }),
        subscriptionId
      );
    }
  }

  private subscribeToMessageService(dataPageName: string, subscriptionId?: string) {
    if (dataPageName && this.subscribedDataPageNames.has(dataPageName)) {
      const entry = this.subscribedDataPageNames.get(dataPageName);
      if (entry) {
        this.subscribedDataPageNames.set(dataPageName, { ...entry, count: entry.count + 1 });
      }
      return;
    }
    const filter = {
      matcher: 'DATAPAGE_UPDATED',
      criteria: {
        datapage: dataPageName
      }
    };

    const messagingSubscriptionId = MessagingService.subscribe(
      filter,
      this.handleMessage.bind(this),
      undefined,
      subscriptionId
    );
    this.subscribedDataPageNames.set(dataPageName, { count: 1, messagingSubscriptionId });
  }

  private isCacheable(dataPageName: string, query?: Query) {
    if (this.isPerInteraction(dataPageName) || !this.isCacheEnabled) {
      return false;
    }
    return !(query && Object.keys(query).length > 1);
  }
}

export default new DataPageUtils();
